/**
 * Clé publique de vérification : obtention et conservation.
 *
 * L'application est inutile sans elle, et le réseau n'est pas garanti. Elle est
 * donc **conservée localement dès la première obtention**, et c'est la copie
 * locale qui sert ensuite : le rafraîchissement est une amélioration, jamais une
 * condition de fonctionnement.
 *
 * `localStorage` et non `sessionStorage`, à l'inverse du poste agent : le
 * terminal de contrôle est personnel et doit rester opérationnel après un
 * redémarrage, sans réseau. Et la donnée n'est pas un secret — c'est une clé
 * publique, qui n'autorise que la vérification.
 */

const CLE_STOCKAGE = 'sygap.verify.clePublique';

export interface ClePubliqueConservee {
  readonly clePublique: string;
  readonly empreinte: string;
  /** Horodatage de l'obtention, en millisecondes. */
  readonly obtenueLe: number;
}

export function lireCleLocale(): ClePubliqueConservee | null {
  const brut = globalThis.localStorage.getItem(CLE_STOCKAGE);
  if (brut === null) return null;
  try {
    const valeur = JSON.parse(brut) as ClePubliqueConservee;
    return typeof valeur.clePublique === 'string' && valeur.clePublique !== '' ? valeur : null;
  } catch {
    globalThis.localStorage.removeItem(CLE_STOCKAGE);
    return null;
  }
}

export function conserverCle(cle: ClePubliqueConservee): void {
  globalThis.localStorage.setItem(CLE_STOCKAGE, JSON.stringify(cle));
}

/**
 * Récupère la clé auprès du serveur.
 *
 * @returns `null` si le réseau est absent ou la réponse inexploitable — cas
 * nominal en mer, pas une erreur.
 */
export async function obtenirCleDistante(racineApi: string): Promise<ClePubliqueConservee | null> {
  try {
    const reponse = await fetch(`${racineApi}/verify/cle-publique`, {
      headers: { accept: 'application/json' },
    });
    if (!reponse.ok) return null;

    const corps = (await reponse.json()) as {
      clePublique?: unknown;
      empreinte?: unknown;
      algorithme?: unknown;
    };
    if (
      corps.algorithme !== 'Ed25519' ||
      typeof corps.clePublique !== 'string' ||
      typeof corps.empreinte !== 'string'
    ) {
      return null;
    }
    return {
      clePublique: corps.clePublique,
      empreinte: corps.empreinte,
      obtenueLe: Date.now(),
    };
  } catch {
    return null;
  }
}

/**
 * Rend la clé à utiliser : la copie locale d'abord, le réseau ensuite.
 *
 * L'ordre compte. Interroger le réseau en premier ferait attendre l'agent à
 * chaque scan, sur une liaison qui n'existe peut-être pas.
 */
export async function obtenirCle(racineApi: string): Promise<ClePubliqueConservee | null> {
  const locale = lireCleLocale();
  if (locale !== null) {
    return locale;
  }
  const distante = await obtenirCleDistante(racineApi);
  if (distante !== null) {
    conserverCle(distante);
  }
  return distante;
}
